import { useEffect, useRef, useState } from 'react'

export type PullState = 'idle' | 'pulling' | 'armed' | 'refreshing' | 'settling'

const TRIGGER = 72 // px of visible pull that arms a refresh
const HOLD = 56 // where the content rests while refreshing
const MAX = 150

/**
 * iOS-style pull to refresh for a scroll container that has native overscroll
 * disabled. Pulling at the top drags `.list-host` down with rubber-band
 * resistance; letting go past the trigger holds it open with a spinner while
 * `onRefresh` runs, then springs back.
 */
export function usePullToRefresh(el: HTMLElement | null, enabled: boolean, onRefresh: () => Promise<unknown>) {
  const [state, setState] = useState<PullState>('idle')
  const [offset, setOffset] = useState(0)
  const live = useRef({ startY: 0, startX: 0, tracking: false, pulling: false, offset: 0, state: 'idle' as PullState })
  const refresh = useRef(onRefresh)
  refresh.current = onRefresh

  useEffect(() => {
    if (!el || !enabled) return
    const s = live.current
    const content = () => el.querySelector<HTMLElement>('.list-host')
    const apply = (value: number, animate: boolean) => {
      s.offset = value
      setOffset(value)
      const node = content()
      if (!node) return
      node.style.transition = animate ? 'transform 380ms cubic-bezier(.2, .9, .25, 1)' : 'none'
      node.style.transform = value ? `translate3d(0, ${value}px, 0)` : ''
    }
    const set = (next: PullState) => { s.state = next; setState(next) }

    const start = (event: TouchEvent) => {
      if (s.state === 'refreshing' || s.state === 'settling' || event.touches.length !== 1) return
      const t = event.touches[0]!
      s.startY = t.clientY; s.startX = t.clientX
      s.tracking = el.scrollTop <= 0
      s.pulling = false
    }
    const move = (event: TouchEvent) => {
      if (!s.tracking) return
      const t = event.touches[0]!
      const dy = t.clientY - s.startY, dx = t.clientX - s.startX
      if (!s.pulling) {
        if (dy < 6 || Math.abs(dx) > Math.abs(dy) || el.scrollTop > 0) { if (dy < 0 || Math.abs(dx) > 10) s.tracking = false; return }
        s.pulling = true
      }
      event.preventDefault()
      // Rubber band: easy at first, stiffer the further you go.
      const pulled = MAX * (1 - 1 / (Math.max(0, dy) / MAX * 0.55 + 1))
      apply(pulled, false)
      const next = pulled >= TRIGGER ? 'armed' : 'pulling'
      if (next !== s.state) {
        if (next === 'armed') navigator.vibrate?.(8)
        set(next)
      }
    }
    const end = async () => {
      if (!s.tracking) return
      s.tracking = false
      if (!s.pulling) return
      s.pulling = false
      if (s.state !== 'armed') { set('settling'); apply(0, true); setTimeout(() => set('idle'), 380); return }
      set('refreshing'); apply(HOLD, true)
      const started = Date.now()
      try { await refresh.current() } catch { /* the list shows its own errors */ }
      const rest = 650 - (Date.now() - started)
      if (rest > 0) await new Promise(resolve => setTimeout(resolve, rest))
      set('settling'); apply(0, true)
      setTimeout(() => set('idle'), 380)
    }
    el.addEventListener('touchstart', start, { passive: true })
    el.addEventListener('touchmove', move, { passive: false })
    el.addEventListener('touchend', end)
    el.addEventListener('touchcancel', end)
    return () => {
      el.removeEventListener('touchstart', start)
      el.removeEventListener('touchmove', move)
      el.removeEventListener('touchend', end)
      el.removeEventListener('touchcancel', end)
    }
  }, [el, enabled])

  return { state, offset, progress: Math.min(1, offset / TRIGGER) }
}

/** True once the large title has scrolled under the navigation bar. */
export function useCollapsed(el: HTMLElement | null, enabled: boolean, at = 56) {
  const [collapsed, setCollapsed] = useState(false)
  useEffect(() => {
    if (!el || !enabled) { setCollapsed(false); return }
    const on = () => setCollapsed(el.scrollTop > at)
    on()
    el.addEventListener('scroll', on, { passive: true })
    return () => el.removeEventListener('scroll', on)
  }, [el, enabled, at])
  return collapsed
}

/** The iOS activity indicator: eight spokes that appear as you pull, then spin. */
export function Spinner({ progress, spinning, size = 26 }: { progress: number; spinning: boolean; size?: number }) {
  const shown = spinning ? 8 : Math.round(progress * 8)
  return (
    <svg className={`ios-spinner${spinning ? ' spinning' : ''}`} width={size} height={size} viewBox="0 0 26 26" aria-hidden="true">
      {Array.from({ length: 8 }, (_, i) => (
        <line key={i} x1="13" y1="3" x2="13" y2="8.5" strokeWidth="2.4" strokeLinecap="round" stroke="currentColor"
          transform={`rotate(${i * 45} 13 13)`} opacity={i < shown ? (spinning ? 0.25 + 0.75 * ((8 - i) % 8) / 8 : 0.75) : 0} />
      ))}
    </svg>
  )
}
